import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FieldValues, useForm } from 'react-hook-form'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { CustomTable } from 'components/customTable'
import { FilterOptions } from 'components/customTable/custom-table-interfaces'
import { SmoothTransition } from 'components/pageTransition'
import { PagePreloader } from 'components/pagePreloader'
import { PLATFORM_OPTIONS } from 'constants/other'
import { useVideoPlayerErrorLog } from 'hooks/player-error-log'
import { parseSearchParams, getUpdatedSearchParams } from 'services/util'

const PLATFORM_FIELD_ID = 'platform' as const

type TableFieldFilter = {
  id?: string
  value?: unknown
}

const getFieldFilterValue = (fields: TableFieldFilter[] = [], id: string) => {
  const matched = fields.find((field) => field?.id === id)
  return matched?.value
}

/** Platform filter is sent as `deviceType`. Omits `search` (not used by this API). */
const buildVideoPlayerErrorRequestParams = (params: Record<string, unknown> = {}) => {
  const { fields = [], ...rest } = params
  const fieldList = Array.isArray(fields) ? (fields as TableFieldFilter[]) : []

  const normalized: Record<string, unknown> = { ...rest }
  delete normalized.search

  const platformValue = getFieldFilterValue(fieldList, PLATFORM_FIELD_ID)
  delete normalized.platform

  if (platformValue !== '' && platformValue !== null && platformValue !== undefined) {
    normalized.deviceType = platformValue
  }

  return normalized
}

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('DD MMM YYYY, hh:mm:ss A') : '-'
}

const VideoPlayerErrorLogs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams: Record<string, any> = parseSearchParams(searchParams, 'filter')
  const [rowSelection, setRowSelection] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)

  const useFormHook = useForm<FieldValues>({
    defaultValues: {
      startDate: filterParams?.fromDate || filterParams?.startDate || null,
      endDate: filterParams?.toDate || filterParams?.endDate || null,
      pagination: filterParams?.pagination || { pageIndex: 0, pageSize: 10 },
      sort: filterParams?.sort || null,
      fields: filterParams?.fields || [],
    },
  })

  const watchedStartDate = useFormHook.watch('startDate') as string | null
  const watchedEndDate = useFormHook.watch('endDate') as string | null

  const mergedFilterParams = useMemo(() => {
    const p: Record<string, any> = { ...(filterParams || {}) }
    if (watchedStartDate) {
      p.fromDate = moment(watchedStartDate).format('YYYY-MM-DD')
    } else {
      delete p.fromDate
    }
    if (watchedEndDate) {
      p.toDate = moment(watchedEndDate).format('YYYY-MM-DD')
    } else {
      delete p.toDate
    }
    return p
  }, [filterParams, watchedStartDate, watchedEndDate])

  const requestParams = useMemo(
    () => buildVideoPlayerErrorRequestParams(mergedFilterParams || {}),
    [mergedFilterParams]
  )

  const { data, isLoading, isFetching, error, refetch } = useVideoPlayerErrorLog(requestParams, {
    keepPreviousData: true,
  })
  const responseData = data?.data

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className="max-w-[200px] truncate font-mono text-xs text-gray-900 dark:text-white"
            title={String(row.original?.id ?? '')}
          >
            {row.original?.id ?? '-'}
          </div>
        ),
      },
      {
        header: 'Device ID',
        accessorKey: 'device_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.device_id ?? '-'}</div>
        ),
      },
      {
        header: 'Device Type',
        accessorKey: 'device_type',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.device_type ?? '-'}</div>
        ),
      },
      {
        header: 'Episode ID',
        accessorKey: 'episode_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.episode_id ?? '-'}</div>
        ),
      },
      {
        header: 'User ID',
        accessorKey: 'user_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.user_id ?? '-'}</div>
        ),
      },
      {
        header: 'Error Code',
        accessorKey: 'error_code',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.error_code ?? '-'}</div>
        ),
      },
      {
        header: 'Error Message',
        accessorKey: 'error_msg',
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className="max-w-md truncate text-gray-900 dark:text-white"
            title={String(row.original?.error_msg ?? '-')}
          >
            {row.original?.error_msg ?? '-'}
          </div>
        ),
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white whitespace-nowrap">
            {formatDateTime(row.original?.createdAt)}
          </div>
        ),
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white whitespace-nowrap">
            {formatDateTime(row.original?.updatedAt)}
          </div>
        ),
      },
    ],
    []
  )

  const filterOptions: FilterOptions[] = useMemo(
    () => [
      {
        key: PLATFORM_FIELD_ID,
        label: 'Device Type',
        options: [{ display: 'All', value: '' }, ...PLATFORM_OPTIONS.map((value) => ({ display: value, value }))],
      },
    ],
    []
  )

  const dateRangeControl = (
    <div className="flex items-center gap-3 justify-end mb-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-300">From</span>
        <input
          type="date"
          className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
          value={watchedStartDate ? moment(watchedStartDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => useFormHook.setValue('startDate', e.target.value || null)}
        />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-300">To</span>
        <input
          type="date"
          className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
          value={watchedEndDate ? moment(watchedEndDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => useFormHook.setValue('endDate', e.target.value || null)}
        />
      </div>
    </div>
  )

  useEffect(() => {
    const subscriber = useFormHook.watch((value, { name }) => {
      if (name) {
        const updatedParams = getUpdatedSearchParams(filterParams, value, name)
        if (Object.keys(updatedParams)?.length) {
          const sp = encodeURIComponent(JSON.stringify(updatedParams))
          setSearchParams({ filter: sp })
        } else {
          setSearchParams({})
        }
      }
    })

    return () => subscriber.unsubscribe()
  }, [filterParams, setSearchParams, useFormHook])

  const handleRefresh = () => {
    setIsRefreshing(true)
    refetch().finally(() => setIsRefreshing(false))
  }

  if (isLoading) return <PagePreloader />

  if (error) {
    return (
      <SmoothTransition>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error: {error instanceof Error ? error.message : 'An error occurred'}
          </div>
        </div>
      </SmoothTransition>
    )
  }

  return (
    <SmoothTransition>
      <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
        <CustomTable
          columns={columns}
          data={responseData?.results || []}
          pages={responseData?.pages || 0}
          rowSelectionState={[rowSelection, setRowSelection]}
          isLoading={isFetching || isRefreshing}
          isErrored={!!error}
          formHook={useFormHook}
          title="Video Player Error Logs"
          onRefresh={handleRefresh}
          totalRecords={responseData?.total}
          titleLabel={`Total ${responseData?.total || 0} records`}
          filterOptions={filterOptions}
          searchVisible={false}
          extraFilterElement={dateRangeControl}
        />
      </div>
    </SmoothTransition>
  )
}

export default VideoPlayerErrorLogs
